import React, { useState } from "react";
import { Sparkles, Handshake, Loader2, Check, AlertTriangle, HelpCircle, ArrowRight, Cpu } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { MatchScore } from "@/components/proptech";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const CONF_STYLE = { HIGH: "bg-emerald-100 text-emerald-700", MEDIUM: "bg-amber-100 text-amber-700", LOW: "bg-slate-100 text-slate-600" };

function Section({ icon: Icon, title, items, tone }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className={`text-[11px] font-semibold uppercase tracking-wide flex items-center gap-1.5 mb-1.5 ${tone}`}><Icon className="h-3.5 w-3.5" /> {title}</div>
      <ul className="space-y-1">{items.map((x, i) => <li key={i} className="text-sm text-slate-600 flex gap-2"><span className="text-slate-300">•</span>{x}</li>)}</ul>
    </div>
  );
}

export function SmartMatchDialog({ buyerId, propertyId, onCollaborate, busy, testid }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/matches/explain?buyer_id=${buyerId}&property_id=${propertyId}`); setData(data); }
    catch (e) { setData({ error: apiError(e) }); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o && !data) load(); }}>
      <DialogTrigger asChild>
        <Button data-testid={testid} variant="outline" className="rounded-xl font-semibold border-accent/40 text-slate-700 hover:bg-accent/5 shrink-0 whitespace-nowrap"><Sparkles className="h-4 w-4 mr-1.5 text-accent" /> Why this matches</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto pc-scroll">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Sparkles className="h-5 w-5 text-accent" /> Smart Match</DialogTitle><DialogDescription className="sr-only">AI-assisted explanation of why this buyer and property match.</DialogDescription></DialogHeader>
        {loading && <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>}
        {data && !loading && !data.error && (
          <div className="space-y-5" data-testid="smart-match-detail">
            <div className="flex items-center gap-4 bg-primary text-primary-foreground rounded-xl p-4">
              <div className="bg-white rounded-full p-0.5"><MatchScore score={data.final_score} size={60} /></div>
              <div className="flex-1 text-sm">
                <div className="flex justify-between"><span className="text-slate-300">Structured match</span><span className="font-semibold">{data.structured_score}</span></div>
                <div className="flex justify-between"><span className="text-slate-300">Semantic fit</span><span className="font-semibold text-accent">{data.semantic_delta >= 0 ? "+" : ""}{data.semantic_delta}</span></div>
                <div className="flex justify-between items-center mt-1"><span className="text-slate-300">Confidence</span><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${CONF_STYLE[data.confidence]}`}>{data.confidence}</span></div>
              </div>
            </div>

            <p className="text-sm text-slate-700 leading-relaxed bg-accent/5 border border-accent/20 rounded-xl p-3">{data.explanation}</p>

            <Section icon={Check} title="Why it matches" items={data.reasons} tone="text-emerald-600" />
            <Section icon={AlertTriangle} title="Compromises" items={data.compromises} tone="text-amber-600" />
            <Section icon={HelpCircle} title="Unknowns to confirm" items={data.unknowns} tone="text-slate-500" />

            {data.next_step && (
              <div className="flex items-start gap-2 text-sm text-slate-700 bg-slate-50 rounded-xl p-3"><ArrowRight className="h-4 w-4 mt-0.5 text-accent shrink-0" /><span><span className="font-semibold">Recommended next step: </span>{data.next_step}</span></div>
            )}

            <div className="flex items-center justify-between pt-1">
              <span className="text-[11px] text-slate-400 flex items-center gap-1"><Cpu className="h-3 w-3" /> {data.source === "ai" ? "AI-assisted explanation" : "Deterministic explanation"}</span>
              {onCollaborate && (
                <Button data-testid="dialog-request-cobroke" onClick={() => { onCollaborate(); setOpen(false); }} disabled={busy} className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-xl font-semibold"><Handshake className="h-4 w-4 mr-1.5" /> Request Co-Broke</Button>
              )}
            </div>
          </div>
        )}
        {data?.error && <p className="text-sm text-red-600 py-6">{data.error}</p>}
      </DialogContent>
    </Dialog>
  );
}
